"use client"

import { useEffect, useRef, useState } from "react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  useStore,
  useStoreOrders,
  useUpdateOrderStatus,
  type Order,
  type OrderStatus,
  type OrderWorkflowStage,
} from "@/api/store"
import { ChevronRight, ShoppingBag, User, Clock, StickyNote } from "lucide-react"
import { useToast } from "@/hooks/use-toast"
import { isSoundMuted } from "@/lib/notification-sound"

const FALLBACK_WORKFLOW: OrderWorkflowStage[] = [
  { key: "pending", label: "Pending", color: "#f59e0b", order: 0, isTerminal: false, isCancellation: false, allowedNext: ["confirmed", "cancelled"] },
  { key: "confirmed", label: "Confirmed", color: "#3b82f6", order: 1, isTerminal: false, isCancellation: false, allowedNext: ["preparing", "cancelled"] },
  { key: "preparing", label: "Preparing", color: "#f97316", order: 2, isTerminal: false, isCancellation: false, allowedNext: ["ready", "cancelled"] },
  { key: "ready", label: "Ready", color: "#22c55e", order: 3, isTerminal: false, isCancellation: false, allowedNext: ["completed", "cancelled"] },
  { key: "completed", label: "Completed", color: "#6b7280", order: 4, isTerminal: true, isCancellation: false, allowedNext: [] },
  { key: "cancelled", label: "Cancelled", color: "#ef4444", order: 5, isTerminal: true, isCancellation: true, allowedNext: [] },
]

function getBadgeStyle(color?: string) {
  if (!color) return {}
  const hasHex = color.startsWith("#")
  return {
    borderColor: color,
    color,
    backgroundColor: hasHex ? `${color}26` : undefined,
  }
}

function getColumnStyle(color?: string) {
  if (!color) return {}
  const hasHex = color.startsWith("#")
  return {
    borderColor: color,
    backgroundColor: hasHex ? `${color}10` : undefined,
    boxShadow: hasHex ? `inset 0 0 0 1px ${color}1F` : undefined,
  }
}

function getCardAccentStyle(color?: string) {
  if (!color) return {}
  return {
    borderLeftColor: color,
  }
}

function playNewOrderSound() {
  try {
    const ctx = new (window.AudioContext || (window as any).webkitAudioContext)()
    const playBeep = (freq: number, start: number, duration: number) => {
      const osc = ctx.createOscillator()
      const gain = ctx.createGain()
      osc.connect(gain)
      gain.connect(ctx.destination)
      osc.frequency.value = freq
      osc.type = "sine"
      gain.gain.setValueAtTime(0.3, ctx.currentTime + start)
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + start + duration)
      osc.start(ctx.currentTime + start)
      osc.stop(ctx.currentTime + start + duration)
    }
    playBeep(880, 0, 0.15)
    playBeep(1100, 0.2, 0.15)
    playBeep(1320, 0.4, 0.25)
  } catch {
    // Browser may block audio without user interaction
  }
}

function getCustomerName(order: Order): string {
  if (typeof order.userId === "object" && order.userId !== null) {
    const u = order.userId as { name?: string; email?: string }
    return u.name || u.email || "Customer"
  }
  return "Customer"
}

function formatTime(dateStr: string) {
  return new Date(dateStr).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
}

interface OrderCardProps {
  order: Order
  stageLabel: string
  stageColor?: string
  onClick: () => void
}

function OrderCard({ order, stageLabel, stageColor, onClick }: OrderCardProps) {
  return (
    <div
      className="group cursor-pointer rounded-xl border border-border bg-card text-card-foreground p-3 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md dark:hover:shadow-lg dark:hover:shadow-black/20 border-l-4"
      style={getCardAccentStyle(stageColor)}
      onClick={onClick}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <ShoppingBag className="h-4 w-4 shrink-0 text-muted-foreground group-hover:text-foreground transition-colors" />
          <span className="font-medium text-sm truncate">#{order._id.slice(-6).toUpperCase()}</span>
        </div>
        <span className="text-[11px] text-muted-foreground shrink-0 rounded-md bg-muted px-1.5 py-0.5">
          {formatTime(order.createdAt)}
        </span>
      </div>

      <div className="mt-2 flex items-center gap-1 text-sm text-muted-foreground">
        <User className="h-3 w-3 shrink-0" />
        <span className="truncate">{getCustomerName(order)}</span>
      </div>

      <div className="mt-2 space-y-0.5 border-t border-border/70 pt-2">
        {order.items.map((item, i) => (
          <div key={i} className="flex justify-between text-xs">
            <span className="truncate">{item.quantity}× {item.title}</span>
            <span className="shrink-0 ml-2">{(item.price * item.quantity).toLocaleString()}</span>
          </div>
        ))}
      </div>

      <div className="mt-2 flex items-center justify-between border-t border-border/70 pt-2">
        <span className="text-xs font-semibold">Total: {order.totalAmount.toLocaleString()}</span>
        <Badge variant="outline" className="text-xs" style={getBadgeStyle(stageColor)}>
          {stageLabel}
        </Badge>
      </div>
    </div>
  )
}

interface OrderDetailDialogProps {
  order: Order | null
  workflow: OrderWorkflowStage[]
  open: boolean
  onOpenChange: (v: boolean) => void
  onStatusUpdate: (id: string, status: OrderStatus) => void
  isUpdating: boolean
}

function OrderDetailDialog({
  order,
  workflow,
  open,
  onOpenChange,
  onStatusUpdate,
  isUpdating,
}: OrderDetailDialogProps) {
  if (!order) return null

  const workflowByKey = new Map(workflow.map((stage) => [stage.key, stage]))
  const currentStage = workflowByKey.get(order.status)
  const nextStages = (currentStage?.allowedNext ?? [])
    .map((key) => workflowByKey.get(key))
    .filter((stage): stage is OrderWorkflowStage => !!stage)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Order #{order._id.slice(-6).toUpperCase()}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <User className="h-4 w-4" />
              <span>{getCustomerName(order)}</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Clock className="h-4 w-4" />
              <span>{new Date(order.createdAt).toLocaleString()}</span>
            </div>
          </div>

          <Badge variant="outline" style={getBadgeStyle(currentStage?.color)}>
            {currentStage?.label ?? order.status}
          </Badge>

          <div>
            <p className="text-sm font-medium mb-2">Items</p>
            <div className="space-y-2">
              {order.items.map((item, i) => (
                <div key={i} className="flex items-center gap-3 rounded-md border border-border bg-muted/30 p-2">
                  {item.mainPhoto && (
                    <img src={item.mainPhoto} alt={item.title} className="h-10 w-10 rounded object-cover" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{item.title}</p>
                    <p className="text-xs text-muted-foreground">{item.price.toLocaleString()} × {item.quantity}</p>
                  </div>
                  <span className="text-sm font-semibold">{(item.price * item.quantity).toLocaleString()}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between border-t pt-2">
            <span className="font-semibold">Total</span>
            <span className="font-bold">{order.totalAmount.toLocaleString()}</span>
          </div>

          <div className="flex items-center gap-2 text-sm">
            <span className="font-medium">Payment:</span>
            <Badge variant="secondary">Pay on Pickup</Badge>
          </div>

          {order.notes && (
            <div className="flex items-start gap-2 rounded-md bg-muted/50 p-2 text-sm">
              <StickyNote className="h-4 w-4 mt-0.5 shrink-0" />
              <span>{order.notes}</span>
            </div>
          )}

          <div className="flex gap-2 pt-2">
            {nextStages.map((nextStage) => (
              <Button
                key={nextStage.key}
                className="flex-1"
                onClick={() => onStatusUpdate(order._id, nextStage.key)}
                disabled={isUpdating}
              >
                {isUpdating ? "Updating..." : nextStage.label}
              </Button>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export function OrdersBoard() {
  const { toast } = useToast()
  const { data: store } = useStore()
  const { data: orders = [], isLoading } = useStoreOrders()
  const updateStatus = useUpdateOrderStatus()

  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null)
  const seenOrderIds = useRef<Set<string>>(new Set())
  const isInitialLoad = useRef(true)

  const workflow = (store?.orderWorkflow?.length ? store.orderWorkflow : FALLBACK_WORKFLOW)
    .slice()
    .sort((a, b) => a.order - b.order)
  const workflowByKey = new Map(workflow.map((stage) => [stage.key, stage]))
  const activeStages = workflow.filter((stage) => !stage.isTerminal)
  const terminalStages = new Set(workflow.filter((stage) => stage.isTerminal).map((stage) => stage.key))
  const entryStage = store?.entryStatusKey
    ? workflowByKey.get(store.entryStatusKey)
    : activeStages[0]

  useEffect(() => {
    if (orders.length === 0) return

    if (isInitialLoad.current) {
      orders.forEach((o) => seenOrderIds.current.add(o._id))
      isInitialLoad.current = false
      return
    }

    const newOrders = orders.filter(
      (o) => o.status === entryStage?.key && !seenOrderIds.current.has(o._id),
    )

    if (newOrders.length > 0) {
      newOrders.forEach((o) => seenOrderIds.current.add(o._id))
      if (!isSoundMuted()) playNewOrderSound()
      toast({
        title: `🛒 ${newOrders.length} new order${newOrders.length > 1 ? "s" : ""}!`,
        description: `Order #${newOrders[0]._id.slice(-6).toUpperCase()} just came in.`,
      })
    }
  }, [entryStage?.key, orders, toast])

  const handleStatusUpdate = async (id: string, status: OrderStatus) => {
    try {
      await updateStatus.mutateAsync({ id, status })
      setSelectedOrder(null)
      toast({ title: "Order updated" })
    } catch {
      toast({ title: "Failed to update order", variant: "destructive" })
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground">
        Loading orders…
      </div>
    )
  }

  const activeOrders = orders.filter((order) => !terminalStages.has(order.status))
  const completedOrders = orders.filter((order) => terminalStages.has(order.status))

  return (
    <>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5">
        {activeStages.map((stage) => {
          const colOrders = activeOrders.filter((o) => o.status === stage.key)
          return (
            <div
              key={stage.key}
              className="rounded-2xl border border-border bg-muted/30 p-3 min-h-[260px] dark:bg-muted/10"
              style={getColumnStyle(stage.color)}
            >
              <div className="mb-3 flex items-center justify-between border-b border-border/70 pb-2">
                <div className="min-w-0">
                  <span className="font-semibold text-sm block truncate">{stage.label}</span>
                  <span className="text-[11px] text-muted-foreground">Active column</span>
                </div>
                <Badge variant="secondary" className="text-xs">{colOrders.length}</Badge>
              </div>
              <div className="space-y-2">
                {colOrders.length === 0 && (
                  <p className="text-xs text-center text-muted-foreground py-8 rounded-lg border border-dashed border-border/70">
                    No orders
                  </p>
                )}
                {colOrders.map((order) => (
                  <OrderCard
                    key={order._id}
                    order={order}
                    stageLabel={stage.label}
                    stageColor={stage.color}
                    onClick={() => setSelectedOrder(order)}
                  />
                ))}
              </div>
            </div>
          )
        })}
      </div>

      {completedOrders.length > 0 && (
        <div className="mt-6">
          <details>
            <summary className="cursor-pointer rounded-lg border border-border bg-muted/40 px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground dark:bg-muted/20">
              <ChevronRight className="inline h-4 w-4 mr-1" />
              Terminal Orders ({completedOrders.length})
            </summary>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {completedOrders.map((order) => {
                const stage = workflowByKey.get(order.status)
                return (
                <OrderCard
                  key={order._id}
                  order={order}
                  stageLabel={stage?.label ?? order.status}
                  stageColor={stage?.color}
                  onClick={() => setSelectedOrder(order)}
                />
                )
              })}
            </div>
          </details>
        </div>
      )}

      <OrderDetailDialog
        order={selectedOrder}
        workflow={workflow}
        open={!!selectedOrder}
        onOpenChange={(v) => !v && setSelectedOrder(null)}
        onStatusUpdate={handleStatusUpdate}
        isUpdating={updateStatus.isPending}
      />
    </>
  )
}
